import React, { useContext, useState, useEffect } from "react";
import { InputNumber, Slider } from "rsuite";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Histogram from "highcharts/modules/histogram-bellcurve";
import HighchartsMore from "highcharts/highcharts-more";
import { SiteContext, LanguageContext } from "../../context";
import { HighchartOptions, removeSeriesTimestamps } from "../../utils";
import { translate } from "../../languages";

Histogram(Highcharts);
HighchartsMore(Highcharts);

export default function SingleHistogramChart({ seriesData, tags }) {
  const { timeZone, site } = useContext(SiteContext);
  const { translations, selectedLanguage } = useContext(LanguageContext);

  const [defaultBinWidth, setDefaultBinWidth] = useState();
  const [binWidth, setBinWidth] = useState();
  const [gap, setGap] = useState();

  HighchartOptions(Highcharts, selectedLanguage, timeZone, translations);

  const seriesId = `tag-${seriesData.id}-${binWidth}`;
  const cleanData = removeSeriesTimestamps(seriesData.data);

  // Set default binWidth & gap
  useEffect(() => {
    const calculateDefaults = () => {
      const defaultBinCount = 30;
      if (seriesData && seriesData.data) {
        const allValues = removeSeriesTimestamps(seriesData.data);
        if (allValues.length > 0) {
          const dataMin = Math.min(...allValues);
          const dataMax = Math.max(...allValues);
          const dataRange = dataMax - dataMin;
          if (dataRange > 0) { return Number((dataRange / defaultBinCount).toFixed(5)); }
        }
      }
    }
    if (gap === undefined) {
      if (sessionStorage.getItem(`${site}-autosave`)) {
        let autosave = JSON.parse(sessionStorage.getItem(`${site}-autosave`));
        setGap(autosave?.histogram?.[seriesData.id]?.gap || 0.1);
      } else {
        setGap(0.1);
      }
    }
    if (binWidth === undefined) {
      if (sessionStorage.getItem(`${site}-autosave`)) {
        let autosave = JSON.parse(sessionStorage.getItem(`${site}-autosave`));
        const defaultBinValue = calculateDefaults();
        setBinWidth(autosave?.histogram?.[seriesData.id]?.binWidth || defaultBinValue);
        setDefaultBinWidth(defaultBinValue);
      } else {
        const defaultBinValue = calculateDefaults();
        setBinWidth(defaultBinValue);
        setDefaultBinWidth(defaultBinValue);
      }
    }
  }, [gap, binWidth, defaultBinWidth, seriesData, site]);

  const saveSessionStorage = (key, id, value) => {
    let autosave = sessionStorage.getItem(`${site}-autosave`);
    let autosaveObj = autosave ? JSON.parse(autosave) : {};
    
    autosaveObj.histogram = autosaveObj.histogram || {};
    autosaveObj.histogram[id] = autosaveObj.histogram[id] || {};
    autosaveObj.histogram[id][key] = value;

    // delete histogram options for tags that are no longer present
    Object.keys(autosaveObj.histogram).forEach(id => {
      if (!tags.includes(Number(id))) {
        delete autosaveObj.histogram[id];
      }
    });

    sessionStorage.setItem(`${site}-autosave`, JSON.stringify(autosaveObj));
  }

   const renderConfigPanel = () => {
    return (
      <div
        style={{
          padding: "10px",
          marginBottom: "1rem",
          borderRadius: "8px",
          width: "200px",
        }}
      >
        <h5>{seriesData.description || `Tag ${seriesData.id}`}</h5>
        {defaultBinWidth &&
          <div style={{ marginTop: "16px" }}>
            <label>{translate("Bin width")}:</label>
            <br />
            <InputNumber
              defaultValue={binWidth}
              onChange={(val) => { setBinWidth(Number(val)); saveSessionStorage("binWidth", Number(seriesData.id), Number(val).toFixed(5)); }}
              min={0}
              step={defaultBinWidth}
              postfix=" units"
              style={{ width: 200 }}
            />
          </div>
        }
        <div style={{marginTop: "16px"}}>
          <label>{translate("Gap between bars")}: {`(${gap?.toFixed(1)})`}</label>
          <br />
          <Slider
            value={gap}
            onChange={(val) => { setGap(val); saveSessionStorage("gap", Number(seriesData.id), Number(val)); }}
            step={0.1}
            min={0.1}
            max={0.5}
            style={{ width: 200, marginTop: 10 }}
            tooltip={false}
          />
        </div>
      </div>
    );
    };

  const buildOptions = () => {
    return {
    chart: { height: 300 },
    title: null,
    xAxis: [
      { title: { text: "Sequence number" }, alignTicks: false },
      { title: { text: "Data value" }, alignTicks: false, opposite: true }
    ],
    yAxis: [
      { title: { text: "Data value" } },
      { title: { text: "Bin value" }, opposite: true }
    ],
    plotOptions: {
      histogram: {
        binWidth: binWidth,
        pointPadding: gap,
        groupPadding: 0,
        borderWidth: 1,
        borderColor: "#ffffff",
        accessibility: {
          point: {
            valueDescriptionFormat:
              "{index}. {point.x:.3f} to {point.x2:.3f}, {point.y}."
          }
        }
      },
      scatter: {
        marker: {
          radius: 2,
          fillColor: "rgba(0,0,0,0.3)"
        }
      },
    },
    series: [
      {
        name: "Data Points",
        type: "scatter",
        data: cleanData,
        id: seriesId,
        marker: { radius: 2 }
      },
      {
        name: "Histogram",
        type: "histogram",
        baseSeries: seriesId,
        xAxis: 1,
        yAxis: 1,
        color: "#7cb5ec"
      }
    ]
  };
  }

  return (
    <div className="d-flex flex-row flex-wrap w-100 align-items-start">
      <div className="p-3 rounded mb-3 me-3">
        {renderConfigPanel()}
      </div>
      <div className="flex-grow-1">
        { gap && 
          <HighchartsReact
            key={binWidth + '-' + gap}
            highcharts={Highcharts}
            options={buildOptions()}
          />
        }
      </div>
    </div>
  );
}
